import * as dataUtils from '../../../../utils/dataUtils';
import * as stringUtils from '../../../../utils/stringUtils';


/**
 * Tree select data operator for filtering, updating
 */
class TreeSelectData {

    #isSingle = false;

    #itemByValue = {};

    #items = [];

    #selectedValues = [];


    /**
     * @param isSingle {boolean} If tree selection is single value only
     * @param items {array|object[]} All items in nested structure
     * @param selectedValues {array|string[]} Pre selected values
     */
    constructor(isSingle, items, selectedValues) {

        this.#isSingle = isSingle;
        this.#items = items;
        this.#itemByValue = this.#buildItemsByValue(items);

        if (isSingle && dataUtils.isSomething(selectedValues) && !dataUtils.isArray(selectedValues)) {
            this.#selectedValues.push(selectedValues);
        } else if (!isSingle && dataUtils.isArray(selectedValues)) {
            this.#selectedValues = selectedValues;
        }
    }


    /**
     * Toggles the given value. If it is a single select, only the given value is retained.
     *
     * @param itemValue {string} Value to toggle: Adds if not set and removes if already set
     * @return {array|string[]} Updated selected values
     */
    updateSelectedValues(itemValue) {

        let updatedSelectedValues = dataUtils.toggleValue(this.#selectedValues, itemValue);
        if (this.#isSingle) {
            updatedSelectedValues = dataUtils.retainValue(updatedSelectedValues, itemValue);
        }
        this.#selectedValues = updatedSelectedValues;

        return this.#selectedValues;
    }


    /**
     * @return {array|string[]} Selected values
     */
    getSelectedValues() {

        return this.#selectedValues;
    }


    /**
     * @param itemValue {string} Value to get item for
     * @return {object} Item for the given value
     */
    getItemByValue(itemValue) {

        return this.#itemByValue[itemValue];
    }


    getItemValues() {

        return this.#items.map(item => item.value);
    }


    /**
     * @return {boolean} True if one or more values are selected; false if not
     */
    hasSelectedValues() {

        return this.#selectedValues.length > 0;
    }


    /**
     * @return {array|string[]|string} Value to propagate respecting isSingle flat (array or single/null value)
     */
    getPropagationValues() {

        if (this.#isSingle) {

            return dataUtils.firstValue(this.#selectedValues);
        }

        return this.#selectedValues;
    }


    /**
     * @param filter {{filterText:string|null}} Filter object
     * @return {array|object[]} Filtered items
     */
    getFilteredItems(filter) {

        if (stringUtils.isEmpty(filter.filterText)) {

            return this.#items;
        }

        return this.#filterItems(this.#items, filter);
    }


    /**
     * @param items {array|object} Items to filter
     * @param filter {{filterText:string|null}} Filter object
     * @return {array|object[]} Filtered items
     */
    #filterItems(items, filter) {

        return items.map(item => {

                const isItemMatching = this.#isItemMatchingFilter(item, filter);
                const filteredItem = {
                    ...item,
                    children: this.#filterItems(item.children || [], filter)
                };

                if (isItemMatching || filteredItem.children.length > 0) {

                    return filteredItem;
                }

                return null;
            })
            .filter(item => item !== null);
    }


    /**
     * @param item {object} Item to filter
     * @param filterText {string|null} Filter text
     * @return {boolean} True if filter is passed; false if not
     */
    #isItemMatchingFilter = (item, {filterText = null}) => {

        const {searchPredicate} = item;
        if (searchPredicate) {

            return searchPredicate(filterText);
        }

        const {content, value} = item;

        return stringUtils.contains(value, filterText)
            || stringUtils.contains(content, filterText);
    };


    /**
     * For fast referencing by value later
     *
     * @param items {array|object[]} Items to map by value
     * @return {object} Item by value
     */
    #buildItemsByValue(items) {

        const itemByValue = {};
        dataUtils.eachRecursively(items, (key, item) => {
            itemByValue[item.value] = item;
        }, 'children');

        return itemByValue;
    }
}


export default TreeSelectData;
